import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { X } from 'lucide-react';
import { createTree, updateTree, type Tree } from '@/api/trees.api';
import { useTreeContextStore } from '@/store/useTreeContextStore';

// ─── Stammbaum anlegen oder umbenennen ──────────────────────────────────────
//
// Eine Maske für beides: die Felder sind dieselben, und ein neuer Baum
// unterscheidet sich vom umbenannten nur darin, dass danach umgeschaltet wird.

interface Props {
  /** null = neuen Baum anlegen. */
  tree: Tree | null;
  onClose: () => void;
}

export function TreeFormModal({ tree, onClose }: Props) {
  const queryClient = useQueryClient();
  const setActiveTree = useTreeContextStore((s) => s.setActiveTree);

  const [name, setName] = useState(tree?.name ?? '');
  const [beschreibung, setBeschreibung] = useState(tree?.description ?? '');

  const mutation = useMutation({
    mutationFn: () => {
      const eingabe = { name: name.trim(), description: beschreibung.trim() || null };
      return tree ? updateTree(tree.id, eingabe) : createTree(eingabe);
    },
    onSuccess: async (gespeichert) => {
      // ERST die Liste neu holen, DANN umschalten. Andersherum sieht der
      // TreeGuard den frischen Baum in seiner noch alten Liste nicht, hält die
      // Auswahl für ungültig und setzt sie sofort wieder auf den ersten Baum
      // zurück – der neue Stammbaum wäre angelegt, aber man landete nicht
      // darin.
      await queryClient.invalidateQueries({ queryKey: ['trees'] });

      if (tree) {
        toast.success('Stammbaum gespeichert.');
      } else {
        // Direkt hineinwechseln: wer gerade einen Stammbaum angelegt hat, will
        // ihn füllen und nicht erst suchen.
        setActiveTree(gespeichert.id);
        toast.success(`„${gespeichert.name}" angelegt.`);
      }
      onClose();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const eingabeKlasse =
    'w-full rounded border border-slate-300 px-2.5 py-1.5 text-sm text-slate-900 focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400';

  return (
    <div
      className="fixed inset-0 z-[1400] flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-lg bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
          <h2 className="text-lg font-semibold text-slate-900">
            {tree ? 'Stammbaum umbenennen' : 'Neuer Stammbaum'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-slate-400 hover:bg-slate-100"
            aria-label="Schließen"
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!name.trim()) {
              toast.error('Name ist erforderlich.');
              return;
            }
            mutation.mutate();
          }}
          className="space-y-4 px-5 py-4"
        >
          <div>
            <label htmlFor="baum-name" className="mb-1 block text-sm font-medium text-slate-700">
              Name *
            </label>
            <input
              id="baum-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="z. B. Familie Müller"
              className={eingabeKlasse}
              autoFocus
            />
          </div>

          <div>
            <label htmlFor="baum-beschreibung" className="mb-1 block text-sm font-medium text-slate-700">
              Beschreibung
            </label>
            <textarea
              id="baum-beschreibung"
              value={beschreibung ?? ''}
              onChange={(e) => setBeschreibung(e.target.value)}
              rows={3}
              className={eingabeKlasse}
            />
          </div>

          {!tree && (
            <p className="rounded-lg bg-slate-50 p-2.5 text-xs leading-relaxed text-slate-500">
              Sie werden Besitzer dieses Stammbaums und entscheiden, wer ihn sehen
              und bearbeiten darf.
            </p>
          )}

          <div className="flex items-center justify-end gap-3 border-t border-slate-200 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={mutation.isPending}
              className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 disabled:opacity-50"
            >
              {mutation.isPending ? 'Speichern…' : tree ? 'Speichern' : 'Anlegen'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
